/**
 * CheckoutDialog — Phase 4 Cleanup presentational extraction.
 *
 * Pure render of the Payment dialog that previously lived inline in
 * `UnifiedSalesPage.js` (lines ~4158–4486).
 *
 * State is OWNED BY THE PAGE for this pass. The dialog only reads from
 * props and invokes the matching setters / callbacks. A future pass
 * (`useCheckoutState`) will collapse the prop surface into a single
 * `cs` object the same way `useHistoricalCredit` does for HC.
 *
 * Boundary discipline:
 *   • No state, no `useEffect`, no API call, no axios import.
 *   • The Page pre-computes `confirmDisabled` so the dialog stays pure.
 *   • The Page owns `onConfirm` (= `handleCreditSale`) which contains the
 *     HC-gate / processSale / Credit-Approval routing.
 *   • Behavior + every existing `data-testid` is preserved verbatim from
 *     the inline version.
 *
 * NOT moved (intentional, per the approved slice):
 *   • Credit Approval dialog (sibling on the page).
 *   • `openCheckout` pre-flight (capital floor, price match, custEdited).
 *   • `processSale` (payload build + API + offline + receipt).
 *   • PaymentTabs decomposition — kept inline here until `useCheckoutState`.
 */
import React from 'react';
import { AlertTriangle, Search, Shield, X, Calendar, Sprout, CheckCircle2, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import CalcInput from './CalcInput';
import { formatPHP } from '../lib/utils';
import TermsPicker from './TermsPicker';

export default function CheckoutDialog({
  // Open / close
  open,
  onOpenChange,

  // Customer surface
  selectedCustomer,
  setSelectedCustomer,
  custSearch,
  setCustSearch,
  customers,
  canViewBalance,

  // Totals
  grandTotal,
  change,

  // Payment state
  paymentType,
  setPaymentType,
  amountTendered,
  setAmountTendered,
  partialPayment,
  setPartialPayment,
  splitCash,
  setSplitCash,
  splitDigital,
  setSplitDigital,
  digitalPlatform,
  setDigitalPlatform,
  digitalRefNumber,
  setDigitalRefNumber,
  digitalSender,
  setDigitalSender,

  // Check payment fields
  checkNumber = '',
  setCheckNumber,
  checkDate = '',
  setCheckDate,
  bankName = '',
  setBankName,

  // Release mode
  releaseMode,
  setReleaseMode,

  // Payment terms — surfaced for credit & partial so the user can't
  // accidentally ship a credit sale without picking a due date.
  terms,
  termsDays,
  amendTerms,        // {days, label} when adding to an existing receipt
  termsOptions,
  transactionDate,
  onTermsChange,

  // Inline crop credit props
  cropCreditEnabled = false,
  creditType = 'by_term',
  onCreditTypeChange,
  cropBlockInfo = null,
  cropBlockLoading = false,
  cropPlantingDate = '',
  onCropPlantingDateChange,
  cropLinkTerms = false,
  onCropLinkTermsChange,
  cropOpenTermInvoices = [],

  // Confirm
  onConfirm,
  saving,
  confirmDisabled,

  // Optional narrowing (2026-08-29) — reuse this dialog for flows that
  // legitimately allow fewer payment types, instead of growing a second
  // payment UI elsewhere. Defaults keep every existing caller identical.
  allowedPaymentTypes = null,   // e.g. ['cash','digital'] for a walk-in add-on
  headerNote = '',              // one line of context above Payment Type
  confirmLabelOverride = '',

  // HC flags + hook handle. `hc` is the `useHistoricalCredit()` return
  // value — needed when isHistoricalCreditMode is true so the inline
  // "reason" field can read/write the same state the underlying
  // HistoricalCreditDialog uses. Default to `null` so the rendering
  // guards (`isHistoricalCreditMode && hc && …`) safely no-op on web
  // builds where the page hasn't been updated to pass it yet.
  isHistoricalCreditMode,
  hc = null,
}) {
  const showCropChoice = cropCreditEnabled && selectedCustomer && (paymentType === 'credit' || paymentType === 'partial');
  const activeCredit = cropBlockInfo?.active_credit;
  const isCropBlocked = cropBlockInfo?.reason === 'expired_season_unpaid';
  const hasActiveSeason = cropBlockInfo?.reason === 'active_crop_credit';
  const termTotal = cropOpenTermInvoices.reduce((sum, inv) => sum + (inv.balance || 0), 0);

  // Harvest date preview (127 days from planting)
  const harvestDate = cropPlantingDate
    ? new Date(new Date(cropPlantingDate).getTime() + 127 * 24 * 60 * 60 * 1000)
        .toLocaleDateString('en-PH', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope' }}>Payment</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto max-h-[calc(85vh-120px)] pr-1">
          {/* Customer display / quick picker */}
          {selectedCustomer ? (
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Customer</p>
                  <p className="font-medium">{selectedCustomer.name}</p>
                </div>
                <button
                  onClick={() => { setSelectedCustomer(null); setCustSearch(''); }}
                  className="text-xs text-slate-400 hover:text-red-500 transition-colors px-2 py-1 rounded"
                  data-testid="clear-customer-btn"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="flex gap-4 mt-1 text-xs text-slate-500">
                {canViewBalance && <span>Balance: <span className={selectedCustomer.balance > 0 ? 'text-red-600 font-medium' : ''}>{formatPHP(selectedCustomer.balance || 0)}</span></span>}
                {canViewBalance && <span>Limit: {formatPHP(selectedCustomer.credit_limit || 0)}</span>}
              </div>
            </div>
          ) : (
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-sm text-slate-500 mb-1.5">Customer</p>
              <div className="relative" data-testid="checkout-customer-picker">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="checkout-customer-search"
                  value={custSearch}
                  onChange={e => {
                    setCustSearch(e.target.value);
                    const match = customers.find(c => c.name.toLowerCase() === e.target.value.toLowerCase());
                    if (match) setSelectedCustomer(match);
                    else setSelectedCustomer(null);
                  }}
                  placeholder="Search customer or type Walk-in..."
                  className="pl-8 h-9 text-sm"
                />
                {custSearch && !selectedCustomer && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-36 overflow-y-auto">
                    {customers
                      .filter(c => c.name.toLowerCase().includes(custSearch.toLowerCase()))
                      .slice(0, 6)
                      .map(c => (
                        <button
                          key={c.id}
                          onClick={() => { setSelectedCustomer(c); setCustSearch(c.name); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex justify-between items-center"
                          data-testid={`checkout-cust-${c.id}`}
                        >
                          <span className="font-medium truncate">{c.name}</span>
                          <span className="text-[10px] text-slate-400 shrink-0 ml-2">
                            {canViewBalance && c.balance > 0 ? `AR: ${formatPHP(c.balance)}` : ''}
                          </span>
                        </button>
                      ))
                    }
                    {customers.filter(c => c.name.toLowerCase().includes(custSearch.toLowerCase())).length === 0 && (
                      <p className="px-3 py-2 text-xs text-slate-400">No matching customers</p>
                    )}
                  </div>
                )}
              </div>
              {!custSearch && <p className="text-xs text-slate-400 mt-1">Walk-in customer (no AR)</p>}
            </div>
          )}

          {/* Total */}
          <div className="text-center py-4">
            <p className="text-sm text-slate-500">Total Amount</p>
            <p className="text-3xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Manrope' }}>{formatPHP(grandTotal)}</p>
          </div>

          {/* Payment Type */}
          <div className="space-y-2">
            <Label className="text-sm">Payment Type</Label>
            {headerNote && (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5"
                 data-testid="checkout-header-note">{headerNote}</p>
            )}
            {(() => {
              const ALL = [
                ['cash', 'Cash'], ['digital', 'Digital'], ['split', 'Split'],
                ['check', 'Check'], ['partial', 'Partial'], ['credit', 'Credit'],
              ];
              const shown = allowedPaymentTypes
                ? ALL.filter(([k]) => allowedPaymentTypes.includes(k))
                : ALL;
              return (
                <Tabs value={paymentType} onValueChange={v => { setPaymentType(v); setDigitalRefNumber(''); setDigitalSender(''); setSplitCash(''); setSplitDigital(''); }}>
                  <TabsList className={`grid w-full grid-cols-${shown.length}`}
                            style={{ gridTemplateColumns: `repeat(${shown.length}, minmax(0, 1fr))` }}>
                    {shown.map(([k, label]) => (
                      <TabsTrigger key={k} value={k} data-testid={`pay-${k}`}>{label}</TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              );
            })()}
            {!selectedCustomer && (paymentType === 'partial' || paymentType === 'credit') && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <AlertTriangle size={12} /> Select a customer first — {paymentType === 'credit' ? 'credit' : 'partial'} balance goes to Accounts Receivable
              </p>
            )}
            {!selectedCustomer && paymentType === 'check' && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <AlertTriangle size={12} /> Select a customer first — check payments must be linked to an account for bounce reversal
              </p>
            )}
          </div>

          {/* Payment inputs */}
          {paymentType === 'cash' && (
            <div className="rounded-xl bg-emerald-50/60 border border-emerald-200 p-3 space-y-2.5"
              data-testid="cash-tender-panel">
              <Label className="text-emerald-900 font-semibold">Amount Tendered</Label>
              <CalcInput
                data-testid="amount-tendered"
                value={String(amountTendered || '')}
                onChange={(v) => setAmountTendered(parseFloat(v) || 0)}
                placeholder={formatPHP(grandTotal)}
                className="text-xl h-14 text-center font-bold bg-white"
              />
              {/* Quick tender: exact + rounded up to 50 / 100 / 500 / 1000
                  (same idea as /terminal), deduped, only amounts ≥ total. */}
              <div className="flex gap-2 flex-wrap">
                {[grandTotal,
                  Math.ceil(grandTotal / 50) * 50,
                  Math.ceil(grandTotal / 100) * 100,
                  Math.ceil(grandTotal / 500) * 500,
                  Math.ceil(grandTotal / 1000) * 1000]
                  .filter((v, i, a) => v >= grandTotal && a.indexOf(v) === i)
                  .slice(0, 5)
                  .map((amt, i) => (
                    <button key={amt} type="button" onClick={() => setAmountTendered(amt)}
                      data-testid={`quick-tender-${i}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        parseFloat(amountTendered) === amt
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-emerald-400'
                      }`}>
                      {amt === grandTotal ? `Exact ${formatPHP(amt)}` : formatPHP(amt)}
                    </button>
                  ))}
              </div>
              {change > 0 && (
                <div className="flex items-center justify-between p-3 bg-emerald-100 border border-emerald-300 rounded-xl">
                  <span className="text-sm text-emerald-800 font-medium">Change</span>
                  <span className="text-2xl font-extrabold text-emerald-700" data-testid="checkout-change">{formatPHP(change)}</span>
                </div>
              )}
            </div>
          )}

          {paymentType === 'digital' && (
            <div className="space-y-3 rounded-xl bg-blue-50 border border-blue-200 p-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                  <span className="text-white text-[10px] font-bold">P</span>
                </div>
                <span className="text-sm font-semibold text-blue-800">Digital Payment {'\u2192'} Digital Wallet</span>
              </div>
              <div>
                <Label className="text-xs text-blue-700">Platform *</Label>
                <select
                  value={digitalPlatform}
                  onChange={e => setDigitalPlatform(e.target.value)}
                  className="w-full mt-1 h-9 rounded-lg border border-blue-200 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  data-testid="digital-platform"
                >
                  {['GCash', 'Maya', 'PayMaya', 'Bank Transfer', 'Instapay', 'Pesonet', 'ShopeePay', 'GrabPay', 'Coins.ph', 'SeaBank', 'Other'].map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs text-blue-700">Reference / Transaction # *</Label>
                <Input
                  value={digitalRefNumber}
                  onChange={e => setDigitalRefNumber(e.target.value)}
                  placeholder="e.g. GC2026XXXXXXXX"
                  autoComplete="off"
                  className="mt-1 h-9 border-blue-200 focus:ring-blue-300"
                  data-testid="digital-ref-number"
                />
              </div>
              <div>
                <Label className="text-xs text-blue-700">Sender Name / Number (optional)</Label>
                <Input
                  value={digitalSender}
                  onChange={e => setDigitalSender(e.target.value)}
                  placeholder="e.g. Juan Dela Cruz / 09XX-XXX-XXXX"
                  autoComplete="off"
                  className="mt-1 h-9 border-blue-200 focus:ring-blue-300"
                  data-testid="digital-sender"
                />
              </div>
              <div className="flex items-center gap-2 text-[10px] text-blue-600 bg-blue-100 rounded-lg px-2.5 py-1.5">
                <span>After sale: QR code will appear to upload the {digitalPlatform} receipt screenshot</span>
              </div>
              <p className="text-lg font-bold text-blue-800 text-center">{formatPHP(grandTotal)}</p>
            </div>
          )}

          {paymentType === 'split' && (
            <div className="space-y-3 rounded-xl bg-gradient-to-br from-emerald-50 to-blue-50 border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-slate-700">Split: Cash + {digitalPlatform}</span>
                <span className="text-xs text-slate-400">Total: {formatPHP(grandTotal)}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-emerald-700">Cash Amount</Label>
                  <CalcInput value={splitCash} data-testid="split-cash"
                    onChange={(v) => { setSplitCash(v); setSplitDigital(String(Math.max(0, grandTotal - (parseFloat(v)||0)))); }}
                    placeholder="0.00" className="mt-1 h-9 border-emerald-200" />
                </div>
                <div>
                  <Label className="text-xs text-blue-700">{digitalPlatform} Amount</Label>
                  <CalcInput value={splitDigital} data-testid="split-digital"
                    onChange={(v) => { setSplitDigital(v); setSplitCash(String(Math.max(0, grandTotal - (parseFloat(v)||0)))); }}
                    placeholder="0.00" className="mt-1 h-9 border-blue-200" />
                </div>
              </div>
              {(parseFloat(splitCash||0) + parseFloat(splitDigital||0)) !== grandTotal && (
                <p className="text-xs text-amber-600 text-center">
                  Cash + Digital must equal {formatPHP(grandTotal)}
                  {' '} (currently {formatPHP(parseFloat(splitCash||0) + parseFloat(splitDigital||0))})
                </p>
              )}
              <div>
                <Label className="text-xs text-blue-700">Platform *</Label>
                <select value={digitalPlatform} onChange={e => setDigitalPlatform(e.target.value)}
                  className="w-full mt-1 h-9 rounded-lg border border-blue-200 bg-white px-2.5 text-sm focus:outline-none">
                  {['GCash', 'Maya', 'PayMaya', 'Bank Transfer', 'Instapay', 'Pesonet', 'ShopeePay', 'GrabPay', 'Other'].map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs text-blue-700">Digital Ref # *</Label>
                <Input value={digitalRefNumber} onChange={e => setDigitalRefNumber(e.target.value)}
                  placeholder="e.g. GC2026XXXXXXXX" autoComplete="off" className="mt-1 h-9 border-blue-200" data-testid="split-ref-number" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Sender (optional)</Label>
                <Input value={digitalSender} onChange={e => setDigitalSender(e.target.value)}
                  placeholder="Name / number" autoComplete="off" className="mt-1 h-9" />
              </div>
              <div className="flex items-center gap-2 text-[10px] text-blue-600 bg-blue-100 rounded-lg px-2.5 py-1.5">
                <span>After sale: QR code will appear to upload the {digitalPlatform} payment screenshot</span>
              </div>
            </div>
          )}

          {paymentType === 'check' && (
            <div className="space-y-3 rounded-xl bg-slate-50 border border-slate-200 p-3">
              <div className="text-xs font-semibold text-slate-700 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold flex items-center justify-center">✓</span>
                Check Payment — treated as cash. Must link to a customer account.
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-600">Check No. *</Label>
                  <Input value={checkNumber}
                    onChange={e => setCheckNumber && setCheckNumber(e.target.value)}
                    placeholder="e.g. 000123"
                    className="mt-1 h-9 font-mono" data-testid="checkout-check-number" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Check Date *</Label>
                  <Input type="date" value={checkDate || transactionDate}
                    onChange={e => setCheckDate && setCheckDate(e.target.value)}
                    className="mt-1 h-9" data-testid="checkout-check-date"
                    title="Date printed on the check. For post-dated checks, enter the future date." />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Bank Name (optional)</Label>
                <Input value={bankName}
                  onChange={e => setBankName && setBankName(e.target.value)}
                  placeholder="BDO, BPI, Metrobank..."
                  className="mt-1 h-9" data-testid="checkout-bank-name" />
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-700 leading-relaxed">
                If this check bounces, the invoice will be converted to Accounts Receivable and the customer will be notified via SMS.
              </div>
              <p className="text-lg font-bold text-slate-800 text-center">{formatPHP(grandTotal)}</p>
            </div>
          )}

          {paymentType === 'partial' && (
            <div className="space-y-3">
              {/* Inline credit type choice — only when crop credit is enabled */}
              {showCropChoice && <InlineCreditTypeSelector
                creditType={creditType}
                onCreditTypeChange={onCreditTypeChange}
                cropBlockInfo={cropBlockInfo}
                cropBlockLoading={cropBlockLoading}
                hasActiveSeason={hasActiveSeason}
                isCropBlocked={isCropBlocked}
                activeCredit={activeCredit}
              />}
              {/* Terms picker. In AMEND mode it renders LOCKED (owner design
                  2026-09-01): the original receipt's term stays pre-selected,
                  every other chip fades — an add-on rides the original term
                  and due date, and the request never sends terms, so a live
                  picker here was a lying control. */}
              {selectedCustomer && onTermsChange && (!showCropChoice || creditType === 'by_term') && (
                amendTerms ? (
                <TermsPicker
                  locked
                  lockedNote="Terms stay as on the original receipt — added items share its due date."
                  value={amendTerms.label}
                  days={amendTerms.days}
                  options={termsOptions}
                  transactionDate={amendTerms.orderDate}
                  onChange={() => {}}
                />
                ) : (
                <TermsPicker
                  value={terms}
                  days={termsDays}
                  options={termsOptions}
                  transactionDate={transactionDate}
                  onChange={onTermsChange}
                />
                )
              )}
              {/* Crop fields — show when "charged_to_crop" is selected */}
              {showCropChoice && creditType === 'charged_to_crop' && (
                <InlineCropFields
                  hasActiveSeason={hasActiveSeason}
                  activeCredit={activeCredit}
                  cropPlantingDate={cropPlantingDate}
                  onCropPlantingDateChange={onCropPlantingDateChange}
                  harvestDate={harvestDate}
                  cropOpenTermInvoices={cropOpenTermInvoices}
                  cropLinkTerms={cropLinkTerms}
                  onCropLinkTermsChange={onCropLinkTermsChange}
                  termTotal={termTotal}
                  customerName={selectedCustomer?.name}
                />
              )}
              {selectedCustomer ? (
                <div>
                  <Label>Amount Paid Now</Label>
                  <CalcInput
                    data-testid="partial-amount"
                    value={String(partialPayment || '')}
                    onChange={(v) => setPartialPayment(Math.min(parseFloat(v) || 0, grandTotal))}
                    className="text-lg h-12"
                  />
                  <div className="flex justify-between mt-2 p-2 bg-amber-50 rounded-lg">
                    <span className="text-sm text-amber-700">Balance (to AR)</span>
                    <span className="font-bold text-amber-700">{formatPHP(grandTotal - partialPayment)}</span>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-700 font-medium">Select a customer above</p>
                  <p className="text-xs text-amber-600 mt-1">Partial payment balance goes to Accounts Receivable and must be assigned to a customer</p>
                </div>
              )}
            </div>
          )}

          {paymentType === 'credit' && (
            <div className="space-y-3">
              {/* Inline credit type choice — only when crop credit is enabled */}
              {showCropChoice && <InlineCreditTypeSelector
                creditType={creditType}
                onCreditTypeChange={onCreditTypeChange}
                cropBlockInfo={cropBlockInfo}
                cropBlockLoading={cropBlockLoading}
                hasActiveSeason={hasActiveSeason}
                isCropBlocked={isCropBlocked}
                activeCredit={activeCredit}
              />}
              {/* Terms picker. In AMEND mode it renders LOCKED (owner design
                  2026-09-01): the original receipt's term stays pre-selected,
                  every other chip fades — an add-on rides the original term
                  and due date, and the request never sends terms, so a live
                  picker here was a lying control. */}
              {selectedCustomer && onTermsChange && (!showCropChoice || creditType === 'by_term') && (
                amendTerms ? (
                <TermsPicker
                  locked
                  lockedNote="Terms stay as on the original receipt — added items share its due date."
                  value={amendTerms.label}
                  days={amendTerms.days}
                  options={termsOptions}
                  transactionDate={amendTerms.orderDate}
                  onChange={() => {}}
                />
                ) : (
                <TermsPicker
                  value={terms}
                  days={termsDays}
                  options={termsOptions}
                  transactionDate={transactionDate}
                  onChange={onTermsChange}
                />
                )
              )}
              {/* Crop fields — show when "charged_to_crop" is selected */}
              {showCropChoice && creditType === 'charged_to_crop' && (
                <InlineCropFields
                  hasActiveSeason={hasActiveSeason}
                  activeCredit={activeCredit}
                  cropPlantingDate={cropPlantingDate}
                  onCropPlantingDateChange={onCropPlantingDateChange}
                  harvestDate={harvestDate}
                  cropOpenTermInvoices={cropOpenTermInvoices}
                  cropLinkTerms={cropLinkTerms}
                  onCropLinkTermsChange={onCropLinkTermsChange}
                  termTotal={termTotal}
                  customerName={selectedCustomer?.name}
                />
              )}
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-sm text-red-700 font-medium">Full Credit Sale</p>
                {selectedCustomer ? (
                  <p className="text-xs text-red-600 mt-1">
                    {formatPHP(grandTotal)} will be added to {selectedCustomer.name}'s receivables
                  </p>
                ) : (
                  <p className="text-xs text-red-600 mt-1">
                    Select a customer above — credit balance must be assigned to an account
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                  <Shield size={12} /> Requires manager approval
                </p>
              </div>
            </div>
          )}

          {/* Stock Release Mode */}
          <div className="space-y-2 pt-1">
            <Label className="text-sm font-semibold text-slate-700">Stock Release</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="release-mode-full"
                onClick={() => setReleaseMode('full')}
                className={`flex flex-col items-start p-3 rounded-lg border-2 text-left transition-all ${
                  releaseMode === 'full'
                    ? 'border-[#1A4D2E] bg-emerald-50'
                    : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                }`}
              >
                <span className={`text-sm font-semibold ${releaseMode === 'full' ? 'text-[#1A4D2E]' : 'text-slate-700'}`}>
                  Full Release
                </span>
                <span className="text-[11px] text-slate-500 mt-0.5">All items given now</span>
              </button>
              <button
                data-testid="release-mode-partial"
                onClick={() => setReleaseMode('partial')}
                className={`flex flex-col items-start p-3 rounded-lg border-2 text-left transition-all ${
                  releaseMode === 'partial'
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                }`}
              >
                <span className={`text-sm font-semibold ${releaseMode === 'partial' ? 'text-amber-700' : 'text-slate-700'}`}>
                  Partial Release
                </span>
                <span className="text-[11px] text-slate-500 mt-0.5">Items released via QR</span>
              </button>
            </div>
            {releaseMode === 'partial' && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                Stock stays reserved until each batch is scanned and released. Use the invoice QR code to release items.
              </p>
            )}
          </div>

          {/* Historical Credit / Notebook AR — inline reason field. */}
          {isHistoricalCreditMode && hc && (
            <div
              data-testid="hc-inline-reason-block"
              className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 space-y-2"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={16} />
                <div className="text-[12px] text-amber-900 leading-snug">
                  <strong>Backdated credit — reason required.</strong>{' '}
                  This will not affect today's cash and requires Owner / Admin
                  TOTP approval on the next step.
                </div>
              </div>
              <textarea
                data-testid="hc-inline-reason-input"
                value={hc.reason}
                onChange={e => hc.setReason(e.target.value)}
                placeholder="Notebook AR carry-forward verified against ledger page 12, customer countersigned 2026-02-04."
                className="w-full text-[12px] rounded border border-amber-300 bg-white px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-400 min-h-[64px]"
              />
              <p className={`text-[11px] font-medium ${
                hc.reason.trim().length >= 20 ? 'text-emerald-700' : 'text-amber-700'
              }`}>
                {hc.reason.trim().length} / 20 minimum
                {hc.reason.trim().length < 20 && ' — keep typing to enable Continue'}
              </p>
            </div>
          )}

          {/* Action buttons — checkout confirm */}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              data-testid="confirm-payment"
              className="flex-1 bg-[#1A4D2E] hover:bg-[#14532d] text-white"
              onClick={onConfirm}
              disabled={confirmDisabled}
              title={
                isHistoricalCreditMode && hc && hc.reason.trim().length < 20
                  ? `Reason must be at least 20 characters (currently ${hc.reason.trim().length})`
                  : undefined
              }
            >
              {saving ? 'Processing...' : (
                confirmLabelOverride ? confirmLabelOverride :
                isHistoricalCreditMode ? 'Continue -> Historical Credit / Notebook AR' :
                paymentType === 'cash' ? 'Complete Sale' :
                paymentType === 'digital' ? `Complete — ${digitalPlatform}` :
                paymentType === 'check' ? 'Complete — Check Payment' :
                paymentType === 'split' ? `Split: P${parseFloat(splitCash||0).toFixed(0)} Cash + P${parseFloat(splitDigital||0).toFixed(0)} ${digitalPlatform}` :
                'Confirm & Create Invoice'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ── Inline Credit Type Selector ─────────────────────────────────────────── */
function InlineCreditTypeSelector({
  creditType,
  onCreditTypeChange,
  cropBlockLoading,
  hasActiveSeason,
  isCropBlocked,
  activeCredit,
}) {
  if (cropBlockLoading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
        <p className="text-xs text-slate-400 flex items-center justify-center gap-1.5">
          <Clock size={12} className="animate-spin" /> Checking crop credit status...
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 space-y-2" data-testid="inline-credit-type-selector">
      <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Credit Type</Label>

      {/* Active season notice */}
      {hasActiveSeason && activeCredit && (
        <div className="flex items-start gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-[11px] text-emerald-700">
          <CheckCircle2 size={12} className="shrink-0 mt-0.5" />
          <div>
            <span className="font-medium">Active crop season</span>
            <span className="ml-1">ends {activeCredit.season_end_date} · {formatPHP(activeCredit.total_due)}</span>
          </div>
        </div>
      )}

      {/* Blocked notice */}
      {isCropBlocked && activeCredit && (
        <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded-lg text-[11px] text-red-700">
          <AlertTriangle size={12} className="shrink-0 mt-0.5" />
          <div>
            <span className="font-medium">Previous crop season unpaid</span>
            <span className="ml-1">({formatPHP(activeCredit.total_due)} outstanding). Settle first.</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        {/* By Term */}
        <button
          type="button"
          data-testid="credit-type-by-term"
          onClick={() => onCreditTypeChange?.('by_term')}
          disabled={hasActiveSeason}
          className={`p-2.5 rounded-lg border-2 text-left transition-all ${
            creditType === 'by_term'
              ? 'border-[#1A4D2E] bg-emerald-50'
              : hasActiveSeason
                ? 'border-slate-200 bg-slate-100 opacity-50 cursor-not-allowed'
                : 'border-slate-200 hover:border-slate-400'
          }`}
        >
          <div className="flex items-center gap-1.5 mb-0.5">
            <Calendar size={13} className={creditType === 'by_term' ? 'text-[#1A4D2E]' : 'text-slate-500'} />
            <span className={`text-xs font-semibold ${creditType === 'by_term' ? 'text-[#1A4D2E]' : 'text-slate-700'}`}>By Term</span>
          </div>
          <p className="text-[10px] text-slate-400 leading-tight">
            {hasActiveSeason ? 'Locked — crop active' : 'Net days due date'}
          </p>
        </button>

        {/* Charged to Crop */}
        <button
          type="button"
          data-testid="credit-type-charged-to-crop"
          onClick={() => onCreditTypeChange?.('charged_to_crop')}
          disabled={isCropBlocked}
          className={`p-2.5 rounded-lg border-2 text-left transition-all ${
            creditType === 'charged_to_crop'
              ? 'border-emerald-500 bg-emerald-50'
              : isCropBlocked
                ? 'border-red-200 bg-red-50 opacity-50 cursor-not-allowed'
                : 'border-slate-200 hover:border-emerald-400'
          }`}
        >
          <div className="flex items-center gap-1.5 mb-0.5">
            <Sprout size={13} className={creditType === 'charged_to_crop' ? 'text-emerald-600' : 'text-slate-500'} />
            <span className={`text-xs font-semibold ${creditType === 'charged_to_crop' ? 'text-emerald-700' : 'text-slate-700'}`}>Charged to Crop</span>
          </div>
          <p className="text-[10px] text-slate-400 leading-tight">
            {hasActiveSeason ? 'Add to active season' : isCropBlocked ? 'Blocked — settle first' : 'Harvest-backed credit'}
          </p>
        </button>
      </div>
    </div>
  );
}


/* ── Inline Crop Fields (planting date, link terms) ─────────────────────── */
function InlineCropFields({
  hasActiveSeason,
  activeCredit,
  cropPlantingDate,
  onCropPlantingDateChange,
  harvestDate,
  cropOpenTermInvoices,
  cropLinkTerms,
  onCropLinkTermsChange,
  termTotal,
  customerName,
}) {
  return (
    <div className="space-y-2">
      {/* Active season — stacking info */}
      {hasActiveSeason && activeCredit && (
        <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700">
          <p className="font-medium flex items-center gap-1.5">
            <CheckCircle2 size={12} /> Adding to active season
          </p>
          <p className="mt-0.5">
            Ends: <strong>{activeCredit.season_end_date}</strong> · Current: <strong>{formatPHP(activeCredit.total_due)}</strong>
          </p>
        </div>
      )}

      {/* New season — planting date */}
      {!hasActiveSeason && (
        <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg space-y-2">
          <p className="text-xs text-emerald-700 font-medium">New crop season for {customerName}</p>
          <div>
            <Label className="text-[11px] text-emerald-700">Planting Date *</Label>
            <Input
              data-testid="planting-date-input"
              type="date"
              value={cropPlantingDate}
              onChange={e => onCropPlantingDateChange?.(e.target.value)}
              className="mt-1 h-8 text-sm"
            />
            {harvestDate && (
              <p className="text-[10px] text-emerald-600 mt-1 flex items-center gap-1">
                <Calendar size={10} /> Expected harvest: <strong>{harvestDate}</strong>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Link existing term invoices */}
      {cropOpenTermInvoices.length > 0 && (
        <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs space-y-2">
          <p className="font-medium text-amber-700">
            {cropOpenTermInvoices.length} open term invoice{cropOpenTermInvoices.length > 1 ? 's' : ''} ({formatPHP(termTotal)})
          </p>
          <p className="text-amber-600">Link to this crop season?</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onCropLinkTermsChange?.(true)}
              className={`flex-1 py-1.5 rounded-lg text-[11px] font-medium border transition-all ${
                cropLinkTerms ? 'bg-amber-600 text-white border-amber-600' : 'border-amber-300 text-amber-700 hover:bg-amber-100'
              }`}
            >
              Yes, link
            </button>
            <button
              type="button"
              onClick={() => onCropLinkTermsChange?.(false)}
              className={`flex-1 py-1.5 rounded-lg text-[11px] font-medium border transition-all ${
                !cropLinkTerms ? 'bg-slate-600 text-white border-slate-600' : 'border-slate-300 text-slate-600 hover:bg-slate-100'
              }`}
            >
              No thanks
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
